import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { DialogComponentBase } from '@wephone-core-ui';
import { IFlexDialogConfig, DialogActionButton, Colors } from '@wephone-utils';
import { PhoneService } from '@wephone-app-phone/services';
import { FormControl } from '@angular/forms';
import { ElectronService } from 'ngx-electron';
import { jsPhoneLib, IJsPhoneDriver } from '@jsphonelib';
import { MatDialogRef } from '@angular/material/dialog';
import { _tk, _ti } from '@wephone-translation';

enum ScreenTest {
  intro = 'intro',
  audio_device = 'audio-device'
}

enum TestStatus {
  notyet = 'notyet',
  recording = 'recording',
  recorded = 'recorded',
  playing = 'playing',
  played = 'played',
}

@Component({
  selector: 'app-test-headset-modal',
  templateUrl: './test-headset-modal.component.html',
  styleUrls: ['./test-headset-modal.component.scss']
})
export class TestHeadsetModalComponent extends DialogComponentBase implements OnInit {
  static modalConfig: IFlexDialogConfig = {
    size: 's',
    width: '400px',
    minHeight: '90%',
  };

  dialogRightActions: DialogActionButton[] = [
    {
      label: _tk('user.title.got_it'),
      action: () => {
        // this.audioDevice();
        this.goNextScreen();
      },
      visible: () => {
        return this.screen === ScreenTest.intro;
      },
      color: Colors.PRIMARY
    },
  ];
  private jsphone: IJsPhoneDriver;
  private recorder;
  private player;
  private pathFull;
  private pathFileRecording;

  private screenSteps: ScreenTest[] = [
    ScreenTest.intro,
    ScreenTest.audio_device,
  ];

  screen = ScreenTest.intro;
  testStatus = TestStatus.notyet;

  audioInputName = new FormControl();
  audioOutputName = new FormControl();
  
  audioInputOptions: any[] = [];
  audioOutputOptions: any[] = [];
  devices: any[] = [];

  sipstackEnabled: boolean;
  showSelect: boolean;
  timerRecording: number = 0;
  timerPlaying: number = 0;

  constructor(
    public phoneService: PhoneService,
    public electronService: ElectronService,
    dialogRef: MatDialogRef<TestHeadsetModalComponent>,
    cdr: ChangeDetectorRef
  ) {
    super(dialogRef, cdr);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.screen = ScreenTest.intro;
    this.testStatus = TestStatus.notyet;

    this.jsphone = jsPhoneLib.getDriver();
    this.audioInputOptions = [];
    this.audioOutputOptions = [];
    this.devices = this.phoneService.getAudioDevices();

    for (const device of this.devices) {
      if (device['is_output'] === 1) {
        this.audioOutputOptions.push(device);
      }
      if (device['is_input'] === 1) {
        this.audioInputOptions.push(device);
      }
    }

    this.showSelect = true;
    this.phoneService.loadPhoneConfig().then(phone_config => {
      this.sipstackEnabled = phone_config.sipstack_enabled;
      const audioInput = phone_config.input_device;
      const audioOutput = phone_config.output_device;
      this.audioInputName.setValue(audioInput);
      this.audioOutputName.setValue(audioOutput);

      this.audioInputName.valueChanges.subscribe(input => {
        return this.changeAudioInput(input);
      });

      this.audioOutputName.valueChanges.subscribe(output => {
        return this.changeAudioOutput(output);
      });
    });
  }

  changeAudioInput(inputDevice: string): void {
    this.phoneService.applyPhoneConfig({ input_device: inputDevice });
  }

  changeAudioOutput(inputDevice: string): void {
    this.phoneService.applyPhoneConfig({ output_device: inputDevice });
  }

  private goNextScreen(): void {
    const idx: number = this.screenSteps.indexOf(this.screen);
    if (idx !== -1 && idx !== this.screenSteps.length + 1) {
      this.screen = this.screenSteps[idx + 1];
    }
    this.detectChanges();
  }

  get hasAudioSettings(): boolean {
    return this.audioInputName.value && this.audioOutputName.value;
  }

  startTesting(): void {
    if (!this.hasAudioSettings) {
      this.loggerService.error('No input or output device');
      return;
    }

    this.startRecording();
  }

  // audioDevice(): void {
  //   this.screen = 'audio-device';
  // }

  goRecording(): void {
    this.stopPlayRecording();
    this.screen = ScreenTest.audio_device;
    this.testStatus = TestStatus.notyet;

    this.detectChanges();
  }

  async startRecording(): Promise<void> {
    this.testStatus = TestStatus.recording;
    this.timerRecording = 0;
    this.timerPlaying = 0;
    this.detectChanges();

    const tmp = window['EREMOTE'].require('tmp');
    tmp.tmpName((err, path) => {
      if (err) { throw err; }
      this.pathFull = path;
      this.recorder = this.jsphone.createRecorder(this.pathFull + 'test-recording.wav');
      this.pathFileRecording = this.pathFull + 'test-recording.wav';

      if (this.recorder) {
        this.jsphone.getCaptureMedia().startTransmitTo(this.recorder);
      }
    });

    for (let i = 0; i < 10; i++) {
      this.timerRecording ++;
      this.detectChanges();
      await this.delay(1000);
    }

    if (this.timerRecording) {
      this.stopRecording();
      this.detectChanges();
    }
  }

  detectChanges(): void {
    if (!this.cdr['destroyed']) {
      super.detectChanges();
    }
  }

  async delay(ms: number): Promise<void> {
    await new Promise(resolve => setTimeout(resolve, ms));
  }

  stopRecording(): void {
    this.testStatus = TestStatus.recorded;
    this.timerRecording = 0;

    if (this.recorder) {
      this.jsphone.getCaptureMedia().stopTransmitTo(this.recorder);
      this.recorder.close();
      this.recorder = undefined;
    }

    this.playRecording();
    this.detectChanges();
  }

  async playRecording(): Promise<void> {
    this.testStatus = TestStatus.playing;
    this.detectChanges();

    this.player = this.jsphone.createPlayer(this.pathFull + 'test-recording.wav');

    if (this.player) {
      this.player.startTransmitTo(this.jsphone.getPlaybackMedia());
    }

    for (let i = 0; i < 10; i++) {
      this.timerPlaying ++;
      this.detectChanges();
      await this.delay(1000);
    }

    if (this.timerPlaying) {
      this.stopPlayRecording();

      this.detectChanges();
    }
  }

  stopPlayRecording(): void {
    this.timerPlaying = 0;
    this.cleanup();
    this.testStatus = TestStatus.played;
    this.detectChanges();
  }

  // replayRecording(): void {
  //   // this.screen = 'stop-recording';
  // }

  // goIntro(): void {
  //   if (this.pathFileRecording) {
  //     const fs = electronRemoteRequire('fs');
  //     fs.unlink(this.pathFileRecording, err => {
  //       if (err) { throw err; }
  //     });
  //   }

  //   this.screen = ScreenTest.intro;
  //   this.detectChanges();
  // }

  private cleanup(): void {
    if (this.recorder) {
      this.recorder.close();
      this.recorder = undefined;
    }
    if (this.player) {
      this.player.startTransmitTo(this.jsphone.getPlaybackMedia());
      this.player.close();
      this.player = undefined;
    }
  }

  testOK(): void {
    this.stopPlayRecording();
    if (this.pathFileRecording) {
      const fs = window['EREMOTE'].require('fs');
      fs.unlink(this.pathFileRecording, err => {
        if (err) { throw err; }
      });
    }

    this.showInfo(_ti('test_headset.message.settings_success'));
    this.dismiss({result: 'ok'});
  }

  restartApp(): void {
    if (this.electronService.isElectronApp) {
      const app = window['EREMOTE'].app;
      app.relaunch();
      app.exit();
    }
  }
}
